import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ArrowIcon } from "./ArrowIcon";
import { track } from "../lib/analytics";
import { askSuccessTeam, formatApiError } from "../lib/api";
import { isValidEmail } from "../lib/dataHygiene";

/**
 * <AskSuccessTeamFab /> — Phase 2f Level-3 help surface.
 *
 * A persistent bottom-right floating button on every user-facing page.
 * Click opens a modal that captures a short free-text message + optional
 * email + auto-captured page context, POSTs to /api/ask-success-team.
 *
 * The component self-hides on admin / auth routes (see <App />'s
 * ChromeGate, which mounts us conditionally).
 */
const HIDE_ROUTE_PREFIXES = [
    "/admin",
    "/signin", "/employer-signin",
    "/portal/login",
];

function auto_context_snapshot(pathname, search) {
    const snap = {
        path: pathname + (search || ""),
        ts: new Date().toISOString(),
        locale: (typeof document !== "undefined" && document.documentElement.lang) || "en",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 250) : "",
    };
    try {
        const params = new URLSearchParams(search || "");
        const invite = params.get("invite");
        if (invite) snap.invitation_token_prefix = invite.slice(0, 6);
        const proposalId = (pathname.match(/\/portal\/proposal\/([^/?#]+)/) || [])[1];
        if (proposalId) snap.proposal_id = proposalId;
        const appId = (pathname.match(/\/admin\/submissions\/([^/?#]+)/) || [])[1];
        if (appId) snap.application_id = appId;
    } catch (_) { /* ignore URL parse failures */ }
    return snap;
}

export default function AskSuccessTeamFab() {
    const { t } = useTranslation();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [message, setMessage] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState(null);
    const triggerRef = useRef(null);
    const textareaRef = useRef(null);

    const shouldHide = HIDE_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p));

    const snapshot = useMemo(
        () => auto_context_snapshot(location.pathname, location.search),
        [location.pathname, location.search],
    );

    useEffect(() => {
        if (!open) return undefined;
        document.body.style.overflow = "hidden";
        setTimeout(() => textareaRef.current?.focus(), 60);
        function onKey(e) {
            if (e.key === "Escape") close();
        }
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const openModal = () => {
        setOpen(true);
        setSent(false);
        setErr(null);
        track("ask_success_team_open", { path: location.pathname });
    };
    const close = () => {
        setOpen(false);
        track("ask_success_team_close", { path: location.pathname, submitted: sent });
        // small delay so the closing tx isn't jarring
        setTimeout(() => triggerRef.current?.focus(), 120);
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (!message.trim()) {
            setErr(t("askHelp.errs.messageRequired"));
            return;
        }
        const trimmedEmail = email.trim();
        if (trimmedEmail && !isValidEmail(trimmedEmail)) {
            setErr(t("auth.common.emailInvalid"));
            return;
        }
        setSubmitting(true);
        try {
            await askSuccessTeam({
                question: message.trim(),
                email: trimmedEmail || undefined,
                context_snapshot: snapshot,
            });
            setSent(true);
            track("ask_success_team_submit", {
                path: location.pathname,
                hasEmail: Boolean(email.trim()),
            });
        } catch (e2) {
            setErr(formatApiError(e2, t("askHelp.errs.generic")));
        } finally {
            setSubmitting(false);
        }
    };

    if (shouldHide) return null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={`ask-fab ${hovered ? "is-hovered" : ""}`}
                onClick={openModal}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onFocus={() => setHovered(true)}
                onBlur={() => setHovered(false)}
                data-testid="ask-fab"
                aria-label={t("askHelp.trigger")}
            >
                <span className="ask-fab-icon" aria-hidden="true">?</span>
                <span className="ask-fab-label">{t("askHelp.trigger")}</span>
            </button>

            {open && (
                <div className="ask-modal-root" data-testid="ask-modal-root">
                    <div
                        className="ask-modal-backdrop"
                        onClick={close}
                        data-testid="ask-modal-backdrop"
                        aria-hidden="true"
                    />
                    <div
                        className="ask-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="ask-modal-title"
                        data-testid="ask-modal"
                    >
                        <button
                            type="button"
                            className="ask-modal-close"
                            onClick={close}
                            aria-label={t("askHelp.close")}
                            data-testid="ask-modal-close"
                        >
                            ×
                        </button>
                        {sent ? (
                            <div className="ask-modal-sent" data-testid="ask-modal-sent">
                                <div className="ask-modal-kicker mono">{t("askHelp.sentLabel")}</div>
                                <h2 id="ask-modal-title">{t("askHelp.sentTitle")}</h2>
                                <p>{t("askHelp.sentBody")}</p>
                                <button
                                    type="button"
                                    className="ask-modal-primary"
                                    onClick={close}
                                    data-testid="ask-modal-done"
                                >
                                    {t("askHelp.sentDone")}
                                    <ArrowIcon direction="right" />
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="ask-modal-form">
                                <div className="ask-modal-kicker mono">{t("askHelp.label")}</div>
                                <h2 id="ask-modal-title">{t("askHelp.title")}</h2>
                                <p className="ask-modal-lede">{t("askHelp.lede")}</p>

                                <label className="ask-modal-field-label">
                                    {t("askHelp.questionLabel")}
                                </label>
                                <textarea
                                    ref={textareaRef}
                                    className="ask-modal-textarea"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder={t("askHelp.questionPlaceholder")}
                                    rows={4}
                                    maxLength={2000}
                                    disabled={submitting}
                                    data-testid="ask-modal-textarea"
                                />

                                <label className="ask-modal-field-label">
                                    {t("askHelp.emailLabel")}
                                </label>
                                <input
                                    type="email"
                                    className="ask-modal-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={(e) => setEmail(e.target.value.trim())}
                                    placeholder={t("askHelp.emailPlaceholder")}
                                    disabled={submitting}
                                    data-testid="ask-modal-email"
                                />
                                <div className="ask-modal-hint">{t("askHelp.emailHint")}</div>

                                {err && (
                                    <div className="ask-modal-err" data-testid="ask-modal-err">{err}</div>
                                )}

                                <button
                                    type="submit"
                                    className="ask-modal-primary"
                                    disabled={submitting || !message.trim()}
                                    data-testid="ask-modal-submit"
                                >
                                    {submitting ? t("askHelp.submitting") : t("askHelp.submit")}
                                    {!submitting && <ArrowIcon direction="right" />}
                                </button>
                                <div className="ask-modal-fine">{t("askHelp.fine")}</div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
